import { lazy, Suspense } from "react";
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
} from "react-router-dom";
import paths from "../utils/paths";
import Loader from "../components/Common/Loader";
import Layout from "../components/Layout";
import LandingPage from "../pages/LandingPage";
import Auth from "../components/Auth";
import ResetPassword from "../pages/ResetPassword";
import ErrorPage from "../components/Common/ErrorPage";

const AdminAuth = lazy(() => import("../components/AdminAuth"));
const AdminLayout = lazy(() => import("../components/AdminLayout"));
const Profile = lazy(() => import("../pages/Profile"));
const EditProfile = lazy(() => import("../pages/Profile/Editprofile"));
const Arbitrators = lazy(() => import("../pages/Arbitrators"));
const LegalDocuments = lazy(() => import("../pages/LegalDocuments"));
const AiChat = lazy(() => import("../pages/AiChat"));
const DigitalDashboard = lazy(() => import("../pages/DigitalDashboard"));
const CreateTemplate = lazy(() => import("../pages/CreateTemplate"));
const EditTemplate = lazy(() => import("../pages/EditTemplate"));
const ArbitratorProfile = lazy(
  () => import("../pages/Arbitrators/ArbitratorProfile")
);
const CreateDocumentWithAi = lazy(
  () => import("../pages/CreateDocumentWithAi")
);
const RegisterPage = lazy(() => import("../pages/RegisterPage"));
const ForgotPassword = lazy(() => import("../pages/ForgotPassword"));
const DigitalDocumentDetails = lazy(
  () => import("../pages/DigitalDocumentDetails")
);
const DigitalDocumentEdit = lazy(() => import("../pages/DigitalDocumentEdit"));
const LegalDocumentDetails = lazy(
  () => import("../pages/LegalDocumentDetails")
);
const DigitalSignaturePrepare = lazy(
  () => import("../pages/DigitalSignaturePrepare")
);
const ApiKeyDashboard = lazy(() => import("../pages/ApiKeyDashboard"));
const ApiDocs = lazy(() => import("../pages/ApiDocs"));
const FAQs = lazy(() => import("../pages/FAQs"));
const DeveloperLayout = lazy(() => import("../components/DeveloperLayout"));
const ApiKeysPage = lazy(() => import("../pages/ApiKeyDashboard/ApiKeysPage"));
const FaqsPage = lazy(() => import("../pages/ApiKeyDashboard/FaqsPage"));
const ContractSetting = lazy(
  () => import("../pages/ApiKeyDashboard/ContractSetting")
);

const AdminDashboard = lazy(() => import("../pages/Admin/AdminDashboard"));
const BlogList = lazy(() => import("../pages/Admin/BlogList"));
const BlogUpload = lazy(() => import("../pages/Admin/BlogUpload"));
const ContactUs = lazy(() => import("../pages/Admin/ContactUs"));
const AdminPricing = lazy(() => import("../pages/Admin/Pricing"));
const AdminFeatures = lazy(() => import("../pages/Admin/Features"));
const Events = lazy(() => import("../pages/Admin/Events"));
const UploadTemplates = lazy(() => import("../pages/Admin/UploadTemplates"));
const LbtAccounts = lazy(() => import("../pages/Admin/LbtAccounts"));
const Partners = lazy(() => import("../pages/Admin/Partners"));
const ManageUserAccounts = lazy(
  () => import("../pages/Admin/ManageUserAccounts")
);
const ApiKeyManagement = lazy(() => import("../pages/Admin/ApiKeyManagement"));
const AdminContractSettings = lazy(
  () => import("../pages/Admin/ContractSettings")
);
const AdminEmailTemplates = lazy(() => import("../pages/Admin/EmailTemplates"));
const PublicDocuments = lazy(() => import("../pages/Admin/PublicDocuments"));

export default function Router() {
  const routingArr = createBrowserRouter([
    {
      path: paths.Root,
      element: <Layout />,
      errorElement: <ErrorPage />,
      children: [
        {
          path: paths.Root,
          element: <LandingPage />,
        },
        {
          path: paths.Register,
          element: <RegisterPage />,
        },
        {
          path: paths.ForgotPassword,
          element: <ForgotPassword />,
        },
        {
          path: paths.FAQs,
          element: <FAQs />,
        },

        {
          path: paths.ResetPassword + "/:token",
          element: <ResetPassword />,
        },
        {
          path: paths.DigitalDocumentEditToken + "/:documentId",
          element: <DigitalDocumentEdit />,
        },
        {
          path: paths.ApiDocsPublic,
          element: <ApiDocs />,
        },
        {
          element: <Auth />,
          children: [
            {
              path: paths.CreateDocumentWithAi,
              element: <CreateDocumentWithAi />,
            },
            {
              path: paths.Chat,
              element: <AiChat />,
            },
            {
              path: paths.LegalDocuments,
              element: <LegalDocuments />,
            },
            {
              path: `${paths.LegalDocuments}/:templateId`,
              element: <LegalDocumentDetails />,
            },
            {
              path: paths.Arbitrators,
              element: <Arbitrators />,
            },
            {
              path: `${paths.Arbitrators}/:advocateId`,
              element: <ArbitratorProfile />,
            },
            {
              path: paths.DigitalDashboard,
              element: <DigitalDashboard />,
            },
            {
              path: paths.DigitalDocumentDetails,
              element: <DigitalDocumentDetails />,
            },
            {
              path: paths.DigitalSignaturePrepare,
              element: <DigitalSignaturePrepare />,
            },
            {
              path: `${paths.DigitalSignaturePrepare}/:documentId`,
              element: <DigitalSignaturePrepare />,
            },
            {
              path: paths.digitalDocumentEdit,
              element: <DigitalDocumentEdit />,
            },
            {
              path: `${paths.digitalDocumentEdit}/:documentId`,
              element: <DigitalDocumentEdit />,
            },
            {
              path: paths.CreateTemplate,
              element: <CreateTemplate />,
            },
            {
              path: `${paths.EditTemplate}/:templateId`,
              element: <EditTemplate />,
            },
            {
              path: paths.Profile,
              element: <Profile key="userProfile" />,
            },
            {
              path: paths.EditProfile,
              element: <EditProfile />,
            },
            {
              path: paths.ApiKeyManagement,
              element: (
                <Suspense fallback={<Loader />}>
                  <DeveloperLayout />
                </Suspense>
              ),
              children: [
                {
                  path: "",
                  element: <Navigate to={paths.ApiKeyDashboard} replace />,
                },
                {
                  path: paths.ApiKeyDashboard,
                  element: <ApiKeyDashboard />,
                },
                {
                  path: paths.ApiKeyKeys,
                  element: <ApiKeysPage />,
                },
                {
                  path: paths.ApiKeyFaqs,
                  element: <FaqsPage />,
                },
                {
                  path: paths.ApiDocs,
                  element: <ApiDocs />,
                },
                {
                  path: paths.ApiKeyContractSetting,
                  element: <ContractSetting />,
                },
              ],
            },
          ],
        },
        {
          path: "*",
          element: <Navigate to={paths.Root} replace />,
        },
      ],
    },
    {
      path: paths.AdminRoot,
      element: (
        <Suspense fallback={<Loader />}>
          <AdminLayout />
        </Suspense>
      ),
      errorElement: <ErrorPage />,
      children: [
        {
          path: "",
          element: (
            <Suspense fallback={<Loader />}>
              <AdminAuth />
            </Suspense>
          ),
          children: [
            {
              index: true,
              element: <AdminDashboard />,
            },
            {
              path: paths.AdminBlogList,
              element: <BlogList />,
            },
            {
              path: paths.AdminBlogUpload,
              element: <BlogUpload key="new" />,
            },
            {
              path: paths.AdminBlogUpload + "/:blogId",
              element: <BlogUpload key="edit" />,
            },
            {
              path: paths.AdminContactUs,
              element: <ContactUs />,
            },
            {
              path: paths.pricing,
              element: <AdminPricing />,
            },
            {
              path: paths.features,
              element: <AdminFeatures />,
            },
            {
              path: paths.events,
              element: <Events />,
            },
            {
              path: paths.partners,
              element: <Partners />,
            },
            {
              path: paths.uploadTemplates,
              element: <UploadTemplates />,
            },
            {
              path: paths.lbtAccounts,
              element: <LbtAccounts />,
            },
            {
              path: paths.manageAccounts,
              element: <ManageUserAccounts />,
            },
            {
              path: paths.AdminApiManagement,
              element: <ApiKeyManagement />,
            },
            {
              path: paths.AdminContractSettings,
              element: <AdminContractSettings />,
            },
            {
              path: paths.AdminEmailTemplates,
              element: <AdminEmailTemplates />,
            },
            {
              path: paths.AdminPublicDocuments,
              element: <PublicDocuments />,
            },
            {
              path: "*",
              element: <Navigate to={paths.Root} replace />,
            },
          ],
        },
      ],
    },
  ]);

  return (
    <Suspense fallback={<Loader />}>
      <RouterProvider router={routingArr} />
    </Suspense>
  );
}
